import { empty } from "../../constants/placeholders"
import { budgetFormat } from "../../utils/number/budget-format"
import type { ColumnUnit, RowRecord, RowsPayload } from "./types"

const dateLocaleFor = (language?: string): string =>
  language?.startsWith("de") ? "de-DE" : "en-US"

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

const formatDate = (value: unknown, language?: string): string => {
  if (typeof value !== "string" && typeof value !== "number") return empty
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  const dateOnly = typeof value === "string" && DATE_ONLY.test(value.trim())
  return new Intl.DateTimeFormat(dateLocaleFor(language), {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(dateOnly ? { timeZone: "UTC" } : {})
  }).format(parsed)
}

const formatNumber = (
  value: unknown,
  maximumFractionDigits: number
): string => {
  const numeric = typeof value === "string" ? Number(value) : value
  if (typeof numeric !== "number" || Number.isNaN(numeric))
    return String(value ?? empty)
  return new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(
    numeric
  )
}

export interface FormatCellOptions {
  currency?: string
  language?: string
}

export const formatCellValue = (
  value: unknown,
  unit: ColumnUnit | undefined,
  { currency, language }: FormatCellOptions = {}
): string => {
  if (value === null || value === undefined || value === "") return empty

  switch (unit) {
    case "currency":
      return budgetFormat(value as number | string, {
        currency: currency || "USD"
      })
    case "count":
      return formatNumber(value, 0)
    case "percent":
      return `${formatNumber(value, 2)}%`
    case "date":
      return formatDate(value, language)
    case "text":
    default:
      return String(value)
  }
}

const toSortableNumber = (value: unknown): number => {
  const numeric = typeof value === "string" ? Number(value) : value
  return typeof numeric === "number" && !Number.isNaN(numeric)
    ? numeric
    : Infinity
}

const toSortableTime = (value: unknown): number => {
  if (typeof value !== "string" && typeof value !== "number") return Infinity
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? Infinity : time
}

export const compareCellValues = (
  a: unknown,
  b: unknown,
  unit: ColumnUnit | undefined
): number => {
  switch (unit) {
    case "currency":
    case "count":
    case "percent":
      return toSortableNumber(a) - toSortableNumber(b)
    case "date":
      return toSortableTime(a) - toSortableTime(b)
    case "text":
    default:
      return String(a ?? "").localeCompare(String(b ?? ""))
  }
}

export const rowsPayloadOf = (payload: {
  table?: RowsPayload
  rows?: RowsPayload
}): RowsPayload | undefined => payload.rows ?? payload.table

export const rowDrillTarget = (row: RowRecord): string | undefined => {
  const target = row.drill_to
  return typeof target === "string" && target ? target : undefined
}

export const rowsToCsvMatrix = (data: RowsPayload): unknown[][] => {
  const header = data.columns.map((col) => col.title)
  const body = data.rows.map((row) =>
    data.columns.map((col) => row[col.key] ?? "")
  )
  return [header, ...body]
}
